import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
  ComposedChart, Bar, Line, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { getAnalysis, listAnalyses } from "../../services/api";
import { ArrowLeftIcon, DownloadIcon } from "../Icons";
import "./Dashboard.css";

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildChartData(histogram, gaussian) {
  return histogram.bins.map((bin, i) => ({
    bin:      parseFloat(bin.toFixed(2)),
    contagem: histogram.counts[i],
    curva:    parseFloat((gaussian.y[i] ?? 0).toFixed(2)),
  }));
}

function mergeChartData(analyses) {
  // Unifica os dados de múltiplas análises para sobreposição
  const allBins = new Set();
  analyses.forEach((a) => a.histogram.bins.forEach((b) => allBins.add(parseFloat(b.toFixed(2)))));
  const bins = Array.from(allBins).sort((a, b) => a - b);

  return bins.map((bin) => {
    const point = { bin };
    analyses.forEach((a) => {
      const idx = a.histogram.bins.findIndex((b) => Math.abs(b - bin) < 0.05);
      point[`${a.label}_contagem`] = idx >= 0 ? a.histogram.counts[idx] : 0;
      point[`${a.label}_curva`]    = idx >= 0 ? parseFloat((a.gaussian.y[idx] ?? 0).toFixed(2)) : 0;
    });
    return point;
  });
}

const COLORS = ["#00d4aa", "#4f8ef7", "#f7a24f", "#f75f5f", "#c47fff"];

// ── Metric Card ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, unit, highlight }) {
  return (
    <div className={`metric-card ${highlight ? "highlight" : ""}`}>
      <span className="metric-label">{label}</span>
      <span className="metric-value">
        {value} <span className="metric-unit">{unit}</span>
      </span>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { id }        = useParams();
  const location      = useLocation();
  const navigate      = useNavigate();
  const [primary,  setPrimary]  = useState(location.state || null);
  const [allAnalyses, setAll]   = useState([]);
  const [compared, setCompared] = useState([]);
  const [loading,  setLoading]  = useState(!location.state);
  const [tab,      setTab]      = useState("individual"); // "individual" | "comparar"

  useEffect(() => {
    if (!primary) {
      getAnalysis(id).then(setPrimary).finally(() => setLoading(false));
    }
    listAnalyses().then(setAll);
  }, [id]);

  const toggleCompare = (a) => {
    setCompared((prev) =>
      prev.find((x) => x.id === a.id)
        ? prev.filter((x) => x.id !== a.id)
        : [...prev, a]
    );
  };

  const loadFull = async (a) => {
    if (!a.histogram) {
      const full = await getAnalysis(a.id);
      setCompared((prev) => prev.map((x) => (x.id === a.id ? full : x)));
      return full;
    }
    return a;
  };

  const handleCompareToggle = async (a) => {
    if (!compared.find((x) => x.id === a.id)) {
      const full = await loadFull(a);
      setCompared((prev) => [...prev.filter((x) => x.id !== a.id), full]);
    } else {
      setCompared((prev) => prev.filter((x) => x.id !== a.id));
    }
  };

  const exportCSV = () => {
    if (!primary) return;
    const rows = [["Diâmetro (mm)"]];
    primary.diameters_valid.forEach((d) => rows.push([d]));
    const csv  = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.download = `${primary.label}.csv`;
    a.click();
  };

  const downloadOriginal = () => {
    if (!primary?.original_image) return;
    const isPng = primary.original_image.startsWith("iVBORw0KGgo");
    const mime  = isPng ? "image/png" : "image/jpeg";
    const ext   = isPng ? "png" : "jpg";
    const a     = document.createElement("a");
    a.href      = `data:${mime};base64,${primary.original_image}`;
    a.download  = `${primary.label}_original.${ext}`;
    a.click();
  };

  if (loading) return <div className="dash-loading">Carregando análise...</div>;
  if (!primary) return <div className="dash-loading">Análise não encontrada.</div>;

  const { metrics } = primary;
  const chartData   = buildChartData(primary.histogram, primary.gaussian);

  // Dados de comparação
  const compareItems = [primary, ...compared.filter((x) => x.id !== primary.id)];
  const compareData  = compareItems.length > 1 ? mergeChartData(compareItems) : null;

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dash-header">
        <button className="btn-back" onClick={() => navigate("/history")}><ArrowLeftIcon size={14} /> Histórico</button>
        <div className="dash-title">
          <h1>{primary.label}</h1>
          <span className="dash-meta">
            {primary.client_type && `${primary.client_type === "campo" ? "Campo" : "Amostra"} · `}
            {primary.location && `${primary.location} · `}
            {primary.batch && `Lote ${primary.batch} · `}
            {new Date(primary.created_at).toLocaleDateString("pt-BR")}
          </span>
        </div>
        {primary.original_image && (
          <button className="btn-export" onClick={downloadOriginal}><DownloadIcon size={14} /> Baixar imagem original</button>
        )}
        <button className="btn-export" onClick={exportCSV}><DownloadIcon size={14} /> CSV</button>
      </div>

      {/* Métricas */}
      <div className="metrics-grid">
        <MetricCard label="Diâmetro médio"  value={metrics.mean_mm}    unit="mm"  highlight />
        <MetricCard label="Desvio padrão"   value={metrics.std_mm}     unit="mm"  />
        <MetricCard label="Coef. variação"  value={`${metrics.cv_percent}%`} unit="" />
        <MetricCard label="Mediana"         value={metrics.median_mm}  unit="mm"  />
        <MetricCard label="Grãos analisados" value={metrics.count}     unit="grãos" />
        <MetricCard label="Outliers removidos" value={primary.outliers_removed} unit="" />
      </div>

      {/* Tabs */}
      <div className="dash-tabs">
        <button
          className={tab === "individual" ? "tab active" : "tab"}
          onClick={() => setTab("individual")}
        >
          Distribuição individual
        </button>
        <button
          className={tab === "comparar" ? "tab active" : "tab"}
          onClick={() => setTab("comparar")}
        >
          Comparar análises
        </button>
      </div>

      {/* Tab: Individual */}
      {tab === "individual" && (
        <div className="chart-card">
          <h2>Distribuição Granulométrica</h2>
          <p className="chart-subtitle">
            μ = {metrics.gauss_mu} mm · σ = {metrics.gauss_sigma} mm
          </p>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
              <CartesianGrid stroke="#1f2d45" strokeDasharray="3 3" />
              <XAxis
                dataKey="bin"
                label={{ value: "Diâmetro (mm)", position: "insideBottom", offset: -4, fill: "#6b7a99" }}
                tick={{ fill: "#6b7a99", fontSize: 12 }}
              />
              <YAxis tick={{ fill: "#6b7a99", fontSize: 12 }} />
              <Tooltip
                contentStyle={{ background: "#111827", border: "1px solid #1f2d45", borderRadius: 8 }}
                labelStyle={{ color: "#e8edf5" }}
                itemStyle={{ color: "#6b7a99" }}
              />
              <Legend wrapperStyle={{ color: "#6b7a99", fontSize: 13 }} />
              <Bar dataKey="contagem" fill="#00d4aa" opacity={0.7} radius={[4,4,0,0]} name="Contagem" />
              <Line dataKey="curva" stroke="#4f8ef7" strokeWidth={2} dot={false} name="Curva gaussiana" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Tab: Comparar */}
      {tab === "comparar" && (
        <div className="compare-section">
          <div className="compare-selector">
            <h3>Selecione análises para comparar</h3>
            <div className="compare-list">
              {allAnalyses
                .filter((a) => a.id !== primary.id)
                .map((a, i) => (
                  <label key={a.id} className="compare-item">
                    <input
                      type="checkbox"
                      checked={!!compared.find((x) => x.id === a.id)}
                      onChange={() => handleCompareToggle(a)}
                    />
                    <span
                      className="compare-dot"
                      style={{ background: COLORS[(i + 1) % COLORS.length] }}
                    />
                    <span>{a.label}</span>
                    <span className="compare-meta">{a.mean_mm} mm · {new Date(a.created_at).toLocaleDateString("pt-BR")}</span>
                  </label>
                ))}
            </div>
          </div>

          {compareData && (
            <div className="chart-card">
              <h2>Comparação de Distribuições</h2>
              <ResponsiveContainer width="100%" height={340}>
                <ComposedChart data={compareData} margin={{ top: 8, right: 24, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke="#1f2d45" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="bin"
                    tick={{ fill: "#6b7a99", fontSize: 12 }}
                    label={{ value: "Diâmetro (mm)", position: "insideBottom", offset: -4, fill: "#6b7a99" }}
                  />
                  <YAxis tick={{ fill: "#6b7a99", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{ background: "#111827", border: "1px solid #1f2d45", borderRadius: 8 }}
                    labelStyle={{ color: "#e8edf5" }}
                  />
                  <Legend wrapperStyle={{ color: "#6b7a99", fontSize: 13 }} />
                  {compareItems.map((a, i) => (
                    <Line
                      key={a.id}
                      dataKey={`${a.label}_curva`}
                      stroke={COLORS[i % COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      name={a.label}
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Imagem anotada */}
      {primary.annotated_image && (
        <div className="chart-card">
          <h2>Imagem processada</h2>
          <p className="chart-subtitle">Verde = grão válido · Vermelho = filtrado por tamanho</p>
          <img
            src={`data:image/jpeg;base64,${primary.annotated_image}`}
            alt="Imagem anotada"
            className="annotated-img"
          />
        </div>
      )}
    </div>
  );
}
